import type React from 'react';
import { useEffect } from 'react';

import { AdornedInput, Adornment, FormField, FormHelp, Label, Paragraph } from '@zillow/constellation';
import { CurrencyInput } from '@zillow/zrs-comet';

import type { Section } from '../../../models/index';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';
import { PRORATED_RENT } from '../../../constants';
import { CURRENCY_INPUT_PLACEHOLDER, MAX_AMOUNT_ERROR_MESSAGE } from '../../../constants/stringConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

const INITIAL_STATE = {
  [PRORATED_RENT.FIELD_NAME.IS_ENABLED]: false,
  [PRORATED_RENT.FIELD_NAME.RENT_AMOUNT]: '',
};

export default function ProratedRent({ section, setSectionValues }: Props): JSX.Element {
  useEffect(() => {
    section.setCustomFieldValidator(PRORATED_RENT.FIELD_NAME.RENT_AMOUNT, isAmountValid);
  }, []);

  function isAmountValid(amount: string): boolean {
    if (Number(amount.replace(/,/g, '')) > PRORATED_RENT.BOUNDS.MAX_AMOUNT) {
      section
        .getField(PRORATED_RENT.FIELD_NAME.RENT_AMOUNT)
        .setErrorMessage(MAX_AMOUNT_ERROR_MESSAGE(PRORATED_RENT.BOUNDS.MAX_AMOUNT));
      return false;
    }
    return true;
  }

  function onNoProrated(): void {
    setSectionValues(INITIAL_STATE);
  }

  function onYesProrated(): void {
    setSectionValues({
      [PRORATED_RENT.FIELD_NAME.IS_ENABLED]: true,
    });
  }

  function onProratedRent(event: React.FormEvent<HTMLInputElement>) {
    const { value } = event.currentTarget;

    setSectionValues({ [PRORATED_RENT.FIELD_NAME.RENT_AMOUNT]: value });
  }

  return (
    <>
      <Paragraph fontType="h6">Will there be any prorated rent when the tenant moves in?</Paragraph>
      <Paragraph marginBottom="sm">
        Prorated rent is charged from move-in until the first full payment’s due date.
      </Paragraph>
      <YesAndNoExpansion
        yesHandler={onYesProrated}
        noHandler={onNoProrated}
        yesActive={section.getFieldValue(PRORATED_RENT.FIELD_NAME.IS_ENABLED)}
      >
        <FormField marginTop="sm">
          <Label id="prorated-rent-amount">Prorated rent amount</Label>
          <AdornedInput
            inputId="AdornedInput-prorated-rent-amount"
            leftAdornment={<Adornment>$</Adornment>}
            input={
              <CurrencyInput
                aria-labelledby="prorated-rent-amount"
                data-test="amount"
                value={section.getFieldValue(PRORATED_RENT.FIELD_NAME.RENT_AMOUNT)}
                placeholder={CURRENCY_INPUT_PLACEHOLDER}
                onChange={onProratedRent}
                onBlur={onProratedRent}
              />
            }
            error={section.getField(PRORATED_RENT.FIELD_NAME.RENT_AMOUNT).hasError}
          />
          {section.getField(PRORATED_RENT.FIELD_NAME.RENT_AMOUNT).hasError && (
            <FormHelp error>{section.getField(PRORATED_RENT.FIELD_NAME.RENT_AMOUNT).getErrorMessage()}</FormHelp>
          )}
          <FormHelp>
            To calculate prorated rent, count the number of days between the move-in date and the due date for the first
            full payment. Multiply that number by the daily rent amount (which is the total monthly rent divided by 30
            days).
          </FormHelp>
        </FormField>
      </YesAndNoExpansion>
    </>
  );
}
